'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@govinity/ui/components/ui/dialog';
import { LoadingSpinner } from '@govinity/ui/components/ui/loading-spinner';
import { LoadingError } from '@govinity/ui/components/ui-custom/loading-error';
import { useToast } from '@govinity/ui/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import api from '@/lib/api';
import {
  hasAvailableEntry,
  hasSelectableEntry,
  publicSpeakerOptionsQueryKey,
  publicSpeakerUserSignupsQueryKey,
  toEntryKey,
} from './helpers';
import { SignupForm } from './signup-form';
import { useUserSignups } from './use-user-signups';
import { useGovernmentContext } from '../../../context';
import { useMeetingContext } from '../meeting-context';

type SignupDialogProps = {
  onOpenChange: (open: boolean) => void;
};

export const SignupDialog = ({ onOpenChange }: SignupDialogProps) => {
  const { government } = useGovernmentContext();
  const { meeting } = useMeetingContext();
  const t = useTranslations('PortalPublicSpeakerSignup');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Existing sign-ups aren't listed here — the meeting page card owns that — but they still gate
  // the options so a citizen can't sign up twice for the same entry.
  const { data: signups, isLoading: isLoadingSignups } = useUserSignups(
    government.code,
    meeting.id
  );

  const {
    data: options,
    error: optionsError,
    isLoading: isLoadingOptions,
  } = useQuery({
    queryKey: publicSpeakerOptionsQueryKey(government.code, meeting.id),
    queryFn: () => api.governments.publicSpeakers(government.code).getById(meeting.id),
    refetchOnMount: 'always',
  });

  const handleSignupSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: publicSpeakerUserSignupsQueryKey(government.code, meeting.id),
    });
    toast({ title: t('signedUpToast') });
    onOpenChange(false);
  };

  const signupForm = () => {
    if (isLoadingOptions || isLoadingSignups) {
      return (
        <div className="flex justify-center py-4">
          <LoadingSpinner size={32} aria-label={t('loadingOptionsAriaLabel')} />
        </div>
      );
    }

    if (optionsError) {
      return <LoadingError message={t('optionsLoadError')} />;
    }

    const sections = options?.sections ?? [];

    if (!hasSelectableEntry(sections)) {
      return <p className="text-muted-foreground text-sm">{t('noEntries')}</p>;
    }

    const signedUpKeys = new Set(
      (signups?.records ?? []).map(({ section, type }) => toEntryKey({ id: section.id, type }))
    );

    if (!hasAvailableEntry(sections, signedUpKeys)) {
      return null;
    }

    return (
      <SignupForm onSuccess={handleSignupSuccess} sections={sections} signedUpKeys={signedUpKeys} />
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('dialogTitle')}</DialogTitle>
          <DialogDescription>{t('dialogDescription')}</DialogDescription>
        </DialogHeader>

        {signupForm()}
      </DialogContent>
    </Dialog>
  );
};
